import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Viewer3dInitOptions, Viewer3dService as Viewer3dSdkService } from '@3ddv/ngx-dvm-internal';
import { Viewer3dLoadView3dOptions, Viewer3dFlagsAPI, Viewer3dPlugins, Viewer3dLoadObject } from '@3ddv/dvm-internal';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { APP_CONFIG } from '@configuration/configuration';
import { catchError, from, Observable, tap } from 'rxjs';
import { VIEWER3D } from '@dvm.providers';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { UiService } from '@services/ui.service';
import { ToastService } from '@services/toast.service';
import { ViewDeterminationService } from './view-determination.service';


@Injectable({
  providedIn: 'root'
})
export class Viewer3dService {

  // CONFIGURATION
  private readonly config:                Signal<Configuration>            = inject(APP_CONFIG);
  private readonly dvmConfig:             Signal<Configuration['Dvm']>     = computed((): Configuration['Dvm'] => this.config().Dvm);
  private readonly uiconfig:              Signal<UiConfiguration>          = inject(UiService).uiConfig;


  // SERVICES
  public readonly viewer3d:               Viewer3dSdkService               = inject(VIEWER3D);
  private readonly toast:                 ToastService                     = inject(ToastService);
  private readonly viewDetermination:     ViewDeterminationService         = inject(ViewDeterminationService);


  // VIEWERS INIT CONFIGURATION
  private viewer3dInitOptions:             Viewer3dInitOptions             = {};


  // VIEWERS LOAD CONFIGURATION
  public viewer3dLoadOptions:              Viewer3dLoadView3dOptions       = {venue_id: '', view_id:''};


  // STATE
  public  readonly is3d:                   WritableSignal<boolean>         = signal<boolean>(this.dvmConfig().viewer.is3d ?? false);


  // GETTERS
  /**
   * Devuelve la vista 3D actualmente cargada en el SDK.
   *
   * @returns Identificador de la vista 3D o `null` si el viewer no esta inicializado.
   */
  public  get currentView3d(): string | null {
    return ( this.viewer3d.isInitialized() && this.viewer3d.getViewId() ) ?
      this.viewer3d.getViewId()! :
      null;
  }


  // PUBLIC METHODS

  /**
   * Inicializa el viewer 3D, configura plugins y flags, y carga la vista inicial si procede.
   */
  public initViewer3d(): void {

    const config = this.dvmConfig().viewer;

    this.setViewer3dLoadOptions(config);

    // Set 3D plugins
    const plugins = this.dvmConfig().misc.plugins;
    this.viewer3dInitOptions.plugins = plugins.viewer3dPlugins?.length ? plugins.viewer3dPlugins : this.setDefault3dPlugins();

    if(!this.viewer3d.isInitialized()) {

      this.viewer3d.initialize(this.viewer3dInitOptions).subscribe({
        next: (): void => {

          this.setViewer3dFlags();

          if (config.is3d && config.viewId) this.load3dView(config.viewId).subscribe();

        },
        error: (error: unknown): void => console.error('Error initializing 3D viewer', error)
      });

    } else if (config.is3d && config.viewId) {

      this.load3dView(config.viewId).subscribe();

    }

  }

  /**
   * Carga una vista 3D con el `viewVersion` configurado y actualiza el estado de vista.
   *
   * @param viewId Identificador de la vista 3D DVM.
   * @returns Observable emitido por el SDK 3D.
   */
  public load3dView(viewId: string): Observable<Viewer3dLoadObject | Error> {

    const viewVersion: string = this.uiconfig().View3dDisplay.viewVersion;
    const options: Viewer3dLoadView3dOptions = Object.assign({}, this.viewer3dLoadOptions, {view_id: viewId});
    if (viewVersion) options.set_id = viewVersion;

    this.toggle3dView(true);

    return from(this.viewer3d.loadView3d(options)).pipe(
      tap((): void => {
        this.viewDetermination.setCurrentView(this.is3d());
      }),
      catchError((): Observable<Error> => {

        const errorMessge: string = 'Error loading 3D view';

        // Si hay error al cargar pano, forzamos 3d view a false y reseteamos view signal
        this.toggle3dView(false);
        this.viewDetermination.setCurrentView(this.is3d());

        // Avisamos al usuario
        this.toast.show(errorMessge, 'error', 2000);
        throw new Error(errorMessge);

      })
    );

  }

  /**
   * Cierra el viewer 3D, resetea el SDK y recalcula la vista activa.
   */
  public close3dView(): void {

    this.toggle3dView(false);

    this.viewer3d.reset();

    this.viewDetermination.setCurrentView(this.is3d());

  }


  // PRIVATE METHODS

  /**
   * Actualiza las opciones de carga 3D con el venue configurado.
   *
   * @param config Configuracion del viewer DVM.
   */
  private setViewer3dLoadOptions(config: Configuration['Dvm']['viewer']): void{

    this.viewer3dLoadOptions.venue_id = config.venueId;

  }

  /**
   * Aplica flags al viewer 3D o restaura los flags por defecto si no se reciben valores.
   *
   * @param flags Flags del SDK 3D que deben sobrescribirse.
   */
  private setViewer3dFlags(flags?: Viewer3dFlagsAPI): void {

    // Default flags
    if(!flags) return this.setDefault3dFlags();

    // Custom flags
    Object.entries(flags).forEach(([key, value]) => {

      if(Object.prototype.hasOwnProperty.call(this.viewer3d.flags, key)){

        this.viewer3d.flags[key as keyof Viewer3dFlagsAPI] = value;

      }

    });

  }

  /**
   * Define los flags base del viewer 3D y del plugin de related previews.
   */
  private setDefault3dFlags(): void {

    this.viewer3d.flags.fixed_aspect_ratio     = false;
    this.viewer3d.flags.show_fullscreen_button = false;

    // RELATED PLUGIN FLAGS
    if(this.viewer3d.related_previews){
      this.viewer3d.related_previews.flags.enable_native_interface = false;
      this.viewer3d.related_previews.flags.automatic_track         = true;
      this.viewer3d.related_previews.flags.enabled                 = true;
    }

  }

  /**
   * Devuelve la lista de plugins base usados por el viewer 3D.
   *
   * @returns Claves de plugins 3D activadas por defecto.
   */
  private setDefault3dPlugins(): (keyof Viewer3dPlugins)[] {

    const plugins: (keyof Viewer3dPlugins)[] = [];

    plugins.push(
      // ...
    );

    return plugins;

  }

  /**
   * Actualiza el signal que indica si el pano 3D esta visible.
   *
   * @param show Nuevo estado visible del viewer 3D.
   */
  private toggle3dView(show: boolean): void {

    if(show !== this.is3d()){

      this.is3d.update(():boolean => show);

    }

  }

}
